import type { EstimateAssumption, EstimatorInput } from "../types/index.js";

/**
 * How complete/specific the user's own answers were - distinct from
 * `EstimatorResult.estimateConfidence`, which describes confidence in the
 * underlying *pricing data* (sample vs. verified). This describes
 * confidence in the *inputs*: a project with several "not sure" answers
 * and a defaulted cabinetry/flooring/appliance selection is a rougher
 * estimate than one where every material and work-type question was
 * answered explicitly, even against the exact same pricing catalog.
 *
 * Per the Phase 5A brief: "Add an estimate confidence rating based on the
 * completeness and specificity of the user's inputs... Explain what
 * information would improve the estimate." This is a generic, engine-
 * level rule (not kitchen-specific) so any project type benefits once it
 * has real questions - it just has more to work with once a project
 * type's rule calculator starts recording "defaulted" assumptions and
 * "unknown" work-type answers, which kitchen (Phase 5A) is the first to
 * exercise in full.
 */
export type InputCompletenessLevel = "preliminary" | "moderate" | "higher_confidence";

export interface InputCompletenessEvaluation {
  level: InputCompletenessLevel;
  /** Plain-language notes on what specifically would sharpen the estimate, one per gap found. Empty when nothing would help. */
  improvementNotes: string[];
}

/**
 * Never itself implies a formal quotation - see the standing "planning-
 * level estimate, not a quotation" trust language on the results screen
 * (packages/estimator-app/src/components/steps/Step7Estimate.tsx); this
 * only ever describes how much the user's own answers narrow the range.
 */
export function evaluateInputCompleteness(input: EstimatorInput, assumptions: EstimateAssumption[]): InputCompletenessEvaluation {
  const improvementNotes: string[] = [];
  let score = 0;

  if (!input.roomSize || input.roomSize.value <= 0) {
    score += 3;
    improvementNotes.push("Provide the space's size (even a rough range) for a much more accurate estimate.");
  }

  const unknownFlags: Array<[string, string]> = [
    [input.workType.plumbingChanges, "Confirm whether plumbing changes are needed."],
    [input.workType.electricalChanges, "Confirm whether electrical changes are needed."],
    [input.workType.structuralChanges, "Confirm whether any walls or structural elements will change."],
    [input.workType.demolitionRequired, "Confirm whether demolition is required."],
    [input.accessibility.required, "Confirm whether accessibility/mobility accommodations are needed."],
    [input.designAndPermits.permitLikelyRequired, "Confirm permit requirements with your municipality."]
  ];
  for (const [value, note] of unknownFlags) {
    if (value === "unknown") {
      score += 2;
      improvementNotes.push(note);
    }
  }

  const defaultedAssumptions = assumptions.filter((a) => a.code.endsWith("_defaulted"));
  for (const assumption of defaultedAssumptions) {
    score += 1;
    improvementNotes.push(assumption.message);
  }

  const level: InputCompletenessLevel = score === 0 ? "higher_confidence" : score <= 3 ? "moderate" : "preliminary";

  return { level, improvementNotes };
}
